<template>
  <div id="BARRING_RESELLING_EDIT" class="offset-bottom-6">
    <div class="container-fixed">
      <p class="offset-top-2" v-html="$t(addOptionalSuffix('barring.editResellingInfotext', crm))"></p>
    </div>

    <VSpinner v-if="isLoading" class="offset-top-2" />
    <VSelection v-else :items="items" :notice="notice" type="reselling" hide-filter-type @open:details="onOpenDetails" @notice="onNotice" />

    <!-- save buttons -->
    <div class="container-fixed">
      <div class="row offset-top-2 offset-bottom-2">
        <div class="col-l-8 col-s-12 offset-bottom-1">
          <p class="offset-left-1 offset-right-2" style="margin-top: -12px" v-html="$t('barring.saveInfotext')"></p>
        </div>
        <div class="col-l-4 col-s-12">
          <section class="pull-right offset-right-1 button-bar">
            <VButton class="btn-clean offset-right-1" :to="{ name: BARRING_RESELLING_STATUS }" label="barring.btnAbort" />
            <VButton class="btn-brand" :disabled="!isChanged || showConfirmation" label="barring.btnSave" @click="onShowConfirmation" />
          </section>
        </div>
      </div>
    </div>
    <div class="container-fixed">
      <VNotification v-if="showConfirmation" type="PROMPT" class="confirmation" lock @close="onHideConfimation">
        <div class="text-center">
          <div v-show="!isSending">
            <!-- we have to use v-show because otherwise the form validation gets removed -->
            <VForm :form="form" optional-validation @submit="onSave">
              <div v-if="isPinRequired" class="row">
                <div class="col-xl-4 col-l-3 col-s-hide col-xs-hide"></div>
                <div class="col-xl-4 col-l-6 col-s-12">
                  <VInput
                    name="pin"
                    :label="$t('barring.legendPin')"
                    :placeholder="$t('barring.placeholderPin')"
                    :title="$t('barring.titlePin')"
                    required
                    type="password"
                  />
                </div>
              </div>
              <p>
                <strong>
                  {{ $t('barring.saveConfirmation') }}
                </strong>
              </p>
              <button v-focus type="submit" class="btn btn-brand btn-small" :title="$t('barring.tooltipBtnYes')">{{ $t('common.yes_continue') }}</button>
              <button type="button" class="btn btn-default btn-small" :title="$t('barring.tooltipBtnNo')" @click="onHideConfimation">
                {{ $t('common.no_cancel') }}
              </button>
            </VForm>
          </div>
          <VSpinner v-if="isSending" class="load-spin offset-top-3" />
        </div>
      </VNotification>
      <VNotification v-else-if="error" type="ERROR" icon="icon-warning icon-large" middle closable @close="error = null">
        <span class="notification-heading" v-html="message" />
      </VNotification>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { sha256 } from 'js-sha256';
import VForm from '@/components/Form/Form';
import VInput from '@/components/Form/Input';
import VButton from '@/components/Button';
import { initFields, focusFirst } from '@/utils/form';
import TitleMixin from '@/mixins/title';
import VSelection from '@/components/Barring/PartnerSelection';
import VNotification from '@/components/Notification';
import focus from '@/directives/focus';
import Proxy from '@/proxies/BarringProfileResellingProxy';
import { BARRING_RESELLING_STATUS, ERROR } from '@route-types';
import VSpinner from '@/components/Spinner';
import { defaultValidators, errorCodeWrongPin } from '../../PinProtection';
import { getErrorMessageKey, addOptionalSuffix } from '@/utils/i18n';

export default {
  name: 'BarringResellingEdit',

  directives: {
    focus,
  },

  components: {
    VButton,
    VSelection,
    VForm,
    VInput,
    VSpinner,
    VNotification,
  },

  mixins: [TitleMixin],

  data() {
    const form = initFields({
      name: 'pin',
      validators: defaultValidators,
    });

    return {
      BARRING_RESELLING_STATUS,
      addOptionalSuffix,
      showConfirmation: false,
      isLoading: true,
      isSending: false,
      modalInfo: null,
      items: null,
      hasPin: null,
      notice: null,
      error: null,
      form,
      previousElement: document.activeElement,
    };
  },

  computed: {
    ...mapState('contract', ['contractHash']),

    ...mapState('barring', ['isChanged']),

    ...mapState('system', ['crm']),

    ...mapGetters('system', ['isBarringPinResetAllowed']),

    isPinRequired() {
      return !!this.hasPin && !this.isBarringPinResetAllowed;
    },

    message() {
      return this.$t(getErrorMessageKey(this.error));
    },
  },

  async created() {
    const response = await new Proxy().edit();
    this.items = response.items;
    this.hasPin = response.hasPin;

    this.form.pin.isHidden = !this.isPinRequired;

    this.isLoading = false;
  },

  methods: {
    onShowConfirmation() {
      this.showConfirmation = true;
      this.previousElement = document.activeElement;
    },

    onHideConfimation() {
      this.showConfirmation = false;
      this.$nextTick(() => {
        this.previousElement.focus();
      });
    },

    onOpenDetails(item) {
      this.modalInfo = item;
    },

    closeConfirmation() {
      this.isSending = false;
      this.showConfirmation = false;
    },

    async onNotice() {
      try {
        await this.$store.dispatch('barring/setChanged', true);
      } catch (error) {
        this.$router.push({ name: ERROR, params: { error } });
      }
    },

    async onSave({ isValid, values, form }) {
      this.error = null;
      if (!isValid) return;
      try {
        this.isSending = true;

        const pin = values.pin ? sha256(values.pin) : null;
        await new Proxy().write(this.items, pin);
        await this.$store.dispatch('barring/setChanged', false);

        this.$router.push({ name: BARRING_RESELLING_STATUS, params: { isSuccess: true } });
      } catch (error) {
        if (error?.data?.message === errorCodeWrongPin) {
          form.pin.setInvalid('barring.errorInvalidPin');
          focusFirst(form);
          this.isSending = false;
        } else if (error?.data?.codeType === 'FATAL') {
          await this.$store.dispatch('barring/setChanged', false);
          this.$router.push({ name: ERROR, params: { error } });
        } else {
          this.error = error;
          this.closeConfirmation();
        }
      }
    },
  },

  beforeRouteLeave(to, from, next) {
    if (this.isChanged) {
      // eslint-disable-next-line
      const answer = window.confirm(this.$t('barring.unsavedChangesNotice'));
      if (answer) {
        this.$store.dispatch('barring/setChanged', false);
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },
};
</script>

<style scoped>
.confirmation button {
  margin-right: 6px;
  margin-left: 6px;
}
.load-spin {
  padding-top: 12px;
  padding-bottom: 12px !important;
  margin: auto;
}
</style>
